import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { MapPin, Plane } from "lucide-react";

export const AirportAutocomplete = ({ value, onSelect, placeholder, testid }) => {
  const [query, setQuery] = useState(value || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const box = useRef(null);
  const timer = useRef(null);

  useEffect(() => { setQuery(value || ""); }, [value]);

  useEffect(() => {
    const onDoc = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const change = (v) => {
    setQuery(v); setOpen(true);
    clearTimeout(timer.current);
    if (!v || v.length < 2) { setResults([]); return; }
    timer.current = setTimeout(async () => {
      try { const { data } = await api.get("/airports", { params: { q: v } }); setResults(data); setActive(0); }
      catch { setResults([]); }
    }, 180);
  };

  const pick = (a) => {
    onSelect(a);
    setQuery(`${a.city || a.name} (${a.code})`);
    setOpen(false);
  };

  const onKey = (e) => {
    if (!open || !results.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => Math.min(i + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); pick(results[active]); }
  };

  return (
    <div className="relative w-full" ref={box}>
      <input
        data-testid={testid}
        className="w-full outline-none text-sm font-medium text-slate-900 placeholder:text-slate-400 bg-transparent"
        placeholder={placeholder}
        value={query}
        onChange={(e) => change(e.target.value)}
        onFocus={() => query.length >= 2 && setOpen(true)}
        onKeyDown={onKey}
        autoComplete="off"
      />
      {open && results.length > 0 && (
        <ul data-testid={`${testid}-list`} className="absolute z-50 left-0 right-0 mt-2 bg-white rounded-xl border border-slate-200 shadow-2xl max-h-72 overflow-y-auto">
          {results.map((a, i) => (
            <li key={a.code}
              onMouseEnter={() => setActive(i)}
              onMouseDown={(e) => { e.preventDefault(); pick(a); }}
              data-testid={`airport-opt-${a.code}`}
              className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer ${i === active ? "bg-slate-100" : ""}`}>
              <Plane className="w-4 h-4 text-[#328CC1] shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-slate-900 truncate">{a.city || a.name} <span className="text-slate-400">· {a.country || a.cc}</span></p>
                <p className="text-xs text-slate-400 truncate flex items-center gap-1"><MapPin className="w-3 h-3" /> {a.name}</p>
              </div>
              <span className="text-xs font-bold text-slate-700 bg-slate-100 rounded px-1.5 py-0.5 shrink-0">{a.code}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
